import { useCallback, useId, useState } from 'react';
import { purposeLabelFromContent, useTranslation } from '../../context/LanguageContext';
import { GOOGLE_CONTACT_SCRIPT_URL } from '../../config/contactChannels';
import { getContactMailtoHref, submitContactToSheet } from '../../utils/submitContactToSheet';
import './contact-form.scss';

const ContactForm = ({ defaultPurpose = '', className = '' }) => {
  const { content } = useTranslation();
  const f = content.contact.form;
  const formId = useId();
  const purposeId = `${formId}-purpose`;
  const [feedback, setFeedback] = useState('idle');
  const [mailtoHref, setMailtoHref] = useState('');

  const allowed = f.purposeOptions.map((o) => o.value);
  const initialPurpose = allowed.includes(defaultPurpose) ? defaultPurpose : '';

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const form = e.target;
      const fd = new FormData(form);
      const name = String(fd.get('name') || '').trim();
      const phone = String(fd.get('phone') || '').trim();
      const purpose = fd.get('purpose');
      const message = String(fd.get('message') || '').trim();
      const label = purposeLabelFromContent(content, purpose);

      setFeedback('submitting');
      setMailtoHref('');
      const result = await submitContactToSheet({ name, phone, topic: label, message });

      if (result.ok) {
        form.reset();
        setFeedback('sent');
        window.setTimeout(() => setFeedback('idle'), 8000);
        return;
      }

      const href = getContactMailtoHref(content, { name, phone, topic: label, message });
      setMailtoHref(href);
      setFeedback('sheetError');
    },
    [content]
  );

  const hasSheet = Boolean(GOOGLE_CONTACT_SCRIPT_URL);
  const isSubmitting = feedback === 'submitting';

  return (
    <form className={`contact-form ${className}`.trim()} onSubmit={handleSubmit}>
      <label className="contact-form__label" htmlFor={`${formId}-name`}>
        {f.name}
      </label>
      <input
        id={`${formId}-name`}
        name="name"
        type="text"
        className="contact-form__input"
        placeholder={f.namePh}
        autoComplete="name"
        required
        minLength={2}
        maxLength={120}
      />

      <label className="contact-form__label" htmlFor={`${formId}-phone`}>
        {f.phone}
      </label>
      <input
        id={`${formId}-phone`}
        name="phone"
        type="tel"
        className="contact-form__input"
        placeholder={f.phonePh}
        autoComplete="tel"
        required
        minLength={8}
        maxLength={22}
        inputMode="tel"
      />

      <label className="contact-form__label" htmlFor={purposeId}>
        {f.topic}
      </label>
      <select
        id={purposeId}
        name="purpose"
        className="contact-form__select"
        required
        defaultValue={initialPurpose || ''}
      >
        <option value="" disabled>
          {f.topicPlaceholder}
        </option>
        {f.purposeOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      <label className="contact-form__label" htmlFor={`${formId}-message`}>
        {f.message}
      </label>
      <textarea
        id={`${formId}-message`}
        name="message"
        className="contact-form__textarea"
        rows={5}
        placeholder={f.messagePh}
        required
        minLength={10}
        maxLength={2000}
      />

      <div className="contact-form__actions">
        <button
          type="submit"
          className="contact-form__submit btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? f.submitting : f.submit}
        </button>
      </div>

      {feedback === 'sent' && (
        <p className="contact-form__status contact-form__status--ok" role="status">
          {f.sent}
        </p>
      )}
      {feedback === 'sheetError' && mailtoHref && (
        <p className="contact-form__status contact-form__status--warn" role="alert">
          {f.sheetFailed}{' '}
          <a className="contact-form__mailto-link" href={mailtoHref}>
            {f.emailInstead}
          </a>
        </p>
      )}

      <p className="contact-form__hint">
        {hasSheet ? (
          <>
            {f.hintSheetBefore}
            <strong>{content.common.mail}</strong>
            {f.hintSheetAfter}
          </>
        ) : (
          <>
            {f.hintEmailBefore}
            <strong>{content.common.mail}</strong>
            {f.hintEmailAfter}
          </>
        )}
      </p>
    </form>
  );
};

export default ContactForm;
